import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Search, Download, SlidersHorizontal, ArrowUpDown, Columns3, MoreVertical, ImageOff, MapPin, Shield, ShieldAlert, ShieldCheck, Clock, KeyRound } from "lucide-react";
import { LISTING_STATUS } from "@avestate/shared";
import { useListings, type ListingRow } from "@/hooks/useListings.js";
import { Button } from "@/components/ui/button.js";
import { RoleGate } from "@/components/RoleGate.js";
import { ComplianceDrawer } from "@/components/ComplianceDrawer.js";
import { KeysDrawer } from "@/components/KeysDrawer.js";
import { KEY_LOCATION_LABEL, type KeyLocation } from "@/hooks/useKeys.js";

function getTitleIt(title: unknown): string {
  if (title && typeof title === "object" && "it" in title) return String((title as { it: unknown }).it ?? "");
  return "";
}
const fmtPrice = (n: number) => "€ " + n.toLocaleString("it-IT");
const fmtDate = (iso: string) => new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short" });

function StatusDot({ row }: { row: ListingRow }): JSX.Element {
  const published = row.published_snapshot_id != null;
  const draft = row.status === "bozza";
  const color = draft ? "bg-ink-mute" : published ? "bg-success" : "bg-accent";
  const label = draft ? "Bozza" : published ? "Pubblicato" : row.status;
  return (
    <span className="inline-flex h-[24px] items-center gap-[7px] whitespace-nowrap rounded-full border border-rule bg-surface-2 px-[10px] text-[11.5px] text-ink-soft">
      <span className={`size-[6px] rounded-full ${color}`} />
      {label}
    </span>
  );
}

function ComplianceBadge({ row, onOpen }: { row: ListingRow; onOpen: () => void }): JSX.Element {
  const c = row.compliance;
  let Icon = Shield;
  let cls = "text-ink-mute/50 hover:text-ink-mute";
  let title = "Adempimenti: checklist non iniziata";
  if (c?.alert) {
    Icon = ShieldAlert;
    cls = "text-risk";
    title = `Adempimenti: ${c.missing_blocking > 0 ? `${c.missing_blocking} ${c.missing_blocking === 1 ? "voce bloccante mancante" : "voci bloccanti mancanti"}` : ""}${c.missing_blocking > 0 && c.expired > 0 ? ", " : ""}${c.expired > 0 ? `${c.expired} ${c.expired === 1 ? "scaduto" : "scaduti"}` : ""}`;
  } else if (c && c.expiring_soon > 0) {
    Icon = Clock;
    cls = "text-warn";
    title = `Adempimenti: ${c.expiring_soon} in scadenza entro 30 giorni`;
  } else if (c) {
    Icon = ShieldCheck;
    cls = "text-success";
    title = "Adempimenti in regola";
  }
  return (
    <button type="button" onClick={onOpen} title={title} aria-label={title} className={`transition-colors ${cls}`}>
      <Icon className="size-[16px]" />
    </button>
  );
}

function KeysBadge({ row, onOpen }: { row: ListingRow; onOpen: () => void }): JSX.Element {
  const k = row.keys;
  let cls = "text-ink-mute/50 hover:text-ink-mute";
  let title = "Chiavi: registro non compilato";
  if (k?.out) {
    cls = "text-warn";
    title = "Chiavi fuori (prese, non ancora restituite)";
  } else if (k) {
    cls = "text-ink-soft hover:text-ink";
    title = `Chiavi: ${KEY_LOCATION_LABEL[k.location as KeyLocation] ?? k.location}`;
  }
  return (
    <button type="button" onClick={onOpen} title={title} aria-label={title} className={`transition-colors ${cls}`}>
      <KeyRound className="size-[15px]" />
    </button>
  );
}

const pill = "inline-flex h-[30px] cursor-pointer items-center gap-[7px] rounded-[5px] border border-rule bg-surface-2 px-[11px] text-[12.5px] text-ink-soft transition-colors hover:border-ink-mute hover:text-ink";

export default function ListingsList(): JSX.Element {
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [q, setQ] = useState<string>("");
  const [checklist, setChecklist] = useState<{ id: string; label: string } | null>(null);
  const [keysFor, setKeysFor] = useState<{ id: string; label: string } | null>(null);
  const filters = useMemo(() => ({ status: statusFilter || undefined, q: q || undefined, page_size: 50 }), [statusFilter, q]);
  const { data, isLoading, error } = useListings(filters);
  const items = data?.items ?? [];

  const stats = useMemo(() => {
    const published = items.filter((i) => i.published_snapshot_id != null).length;
    const drafts = items.filter((i) => i.status === "bozza").length;
    const value = items.reduce((s, i) => s + (i.prezzo ?? 0), 0);
    return { total: data?.total ?? items.length, published, drafts, value };
  }, [items, data?.total]);

  return (
    <div className="space-y-[22px]">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-[27px] font-bold tracking-tight text-ink">Immobili</h1>
          <p className="mt-[5px] text-[13.5px] text-ink-soft">
            {data ? `${stats.total} ${stats.total === 1 ? "immobile" : "immobili"} nel portfolio` : isLoading ? "Carico…" : "—"}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button className={pill} style={{ height: 34 }}><Download className="size-[15px]" /> Esporta CSV</button>
          <RoleGate min="editor">
            <Button asChild><Link to="/listings/new"><Plus className="size-4" /> Nuovo immobile</Link></Button>
          </RoleGate>
        </div>
      </header>

      {/* stat strip */}
      <div className="grid grid-cols-2 overflow-hidden rounded-[5px] border border-rule bg-surface sm:grid-cols-4">
        <Stat v={String(stats.total)} l="Totali" delta="+2 questo mese" />
        <Stat v={String(stats.published)} l="Pubblicati" />
        <Stat v={String(stats.drafts)} l="Bozze" />
        <Stat v={`€${(stats.value / 1e6).toFixed(2).replace(".", ",")}`} unit="M" l="Valore archivio" last />
      </div>

      {error ? (
        <p className="rounded-[5px] border border-risk/30 bg-risk/10 p-3 text-[13px] text-risk">Errore: {(error as Error).message}</p>
      ) : null}

      {/* panel with corner ticks */}
      <div className="ticks relative rounded-[5px] border border-rule bg-surface">
        <div className="flex flex-wrap items-center gap-[9px] border-b border-rule p-[12px_14px]">
          <div className="flex h-[30px] items-center gap-[7px] rounded-[5px] border border-rule bg-surface-2 px-[11px]">
            <Search className="size-[13px] text-ink-mute" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Cerca per slug…"
              aria-label="Cerca per slug"
              className="w-[180px] bg-transparent text-[12.5px] text-ink outline-none placeholder:text-ink-mute"
            />
          </div>
          <label className={pill}>
            <SlidersHorizontal className="size-[13px]" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              aria-label="Filtra per stato"
              className="cursor-pointer bg-transparent text-[12.5px] text-ink-soft outline-none [&>option]:bg-surface [&>option]:text-ink"
            >
              <option value="">Stato · tutti</option>
              {LISTING_STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <span className={pill}><ArrowUpDown className="size-[13px]" /> Ordina · aggiornato</span>
          <span className={`${pill} ml-auto`}><Columns3 className="size-[13px]" /> Colonne</span>
        </div>

        <table className="w-full border-collapse">
          <thead>
            <tr>
              <Th className="w-[34px]"><span className="inline-block size-[15px] rounded-[4px] border border-rule bg-surface-2" /></Th>
              <Th>Immobile</Th>
              <Th>Stato</Th>
              <Th>Località</Th>
              <Th className="text-right">Prezzo</Th>
              <Th>APE</Th>
              <Th className="w-[76px]">Doc · chiavi</Th>
              <Th className="text-right">Aggiornato</Th>
              <Th className="w-[40px]"> </Th>
            </tr>
          </thead>
          <tbody>
            {isLoading
              ? Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i} className="border-b border-rule-soft">
                    <td className="p-[12px_14px]"><span className="inline-block size-[15px] rounded-[4px] border border-rule bg-surface-2" /></td>
                    <td className="p-[12px_14px]"><div className="flex items-center gap-[13px]"><div className="h-[40px] w-[54px] animate-pulse rounded-[5px] bg-surface-2" /><div className="h-3 w-40 animate-pulse rounded bg-surface-2" /></div></td>
                    <td colSpan={7} className="p-[12px_14px]"><div className="h-3 w-24 animate-pulse rounded bg-surface-2" /></td>
                  </tr>
                ))
              : items.map((row) => {
                  const title = getTitleIt(row.title);
                  return (
                    <tr key={row.id} className="border-b border-rule-soft transition-colors last:border-b-0 hover:bg-surface-2">
                      <td className="p-[12px_14px]"><span className="inline-block size-[15px] rounded-[4px] border border-rule bg-surface-2" /></td>
                      <td className="p-[12px_14px]">
                        <Link to={`/listings/${row.id}`} className="group flex min-w-[260px] items-center gap-[13px]">
                          {row.cover_url ? (
                            <img src={row.cover_url} alt="" loading="lazy" className="h-[40px] w-[54px] flex-none rounded-[5px] border border-rule object-cover" />
                          ) : (
                            <span className="grid h-[40px] w-[54px] flex-none place-items-center rounded-[5px] border border-rule bg-surface-2 text-ink-mute"><ImageOff className="size-4" /></span>
                          )}
                          <span className="min-w-0">
                            <span className="block truncate text-[13.5px] font-semibold text-ink group-hover:text-accent">{title || row.slug}</span>
                            <span className="block truncate font-mono text-[11px] text-ink-mute">{row.slug}</span>
                          </span>
                        </Link>
                      </td>
                      <td className="p-[12px_14px]"><StatusDot row={row} /></td>
                      <td className="p-[12px_14px]">
                        <span className="inline-flex items-center gap-[6px] whitespace-nowrap text-[13px] text-ink-soft">
                          <MapPin className="size-[13px] text-ink-mute" />{row.city ?? "—"}{row.zone ? <span className="text-ink-mute"> · {row.zone}</span> : null}
                        </span>
                      </td>
                      <td className="whitespace-nowrap p-[12px_14px] text-right font-mono text-[13.5px] font-semibold text-ink">
                        {row.prezzo != null ? fmtPrice(row.prezzo) : <span className="text-ink-mute">—</span>}
                      </td>
                      <td className="p-[12px_14px]">
                        {row.ape ? <span className="grid h-[22px] w-[30px] place-items-center rounded-[4px] border border-rule bg-surface-2 font-mono text-[11px] text-ink-soft">{row.ape}</span> : <span className="text-ink-mute">—</span>}
                      </td>
                      <td className="p-[12px_14px]">
                        <span className="inline-flex items-center gap-[9px]">
                          <ComplianceBadge row={row} onOpen={() => setChecklist({ id: row.id, label: title || row.slug })} />
                          <KeysBadge row={row} onOpen={() => setKeysFor({ id: row.id, label: title || row.slug })} />
                        </span>
                      </td>
                      <td className="whitespace-nowrap p-[12px_14px] text-right font-mono text-[11.5px] text-ink-mute">{fmtDate(row.updated_at)}</td>
                      <td className="p-[12px_14px]"><Link to={`/listings/${row.id}`} className="text-ink-mute hover:text-ink"><MoreVertical className="size-4" /></Link></td>
                    </tr>
                  );
                })}
            {!isLoading && items.length === 0 ? (
              <tr><td colSpan={9} className="p-16 text-center text-ink-mute">Nessun immobile. <Link to="/listings/new" className="text-accent hover:underline">Creane uno</Link>.</td></tr>
            ) : null}
          </tbody>
        </table>

        <div className="flex items-center justify-between border-t border-rule p-[12px_14px]">
          <span className="font-mono text-[11.5px] text-ink-mute">1–{items.length} di {stats.total}</span>
          <div className="flex gap-[6px]">
            <PgBtn>‹</PgBtn><PgBtn active>1</PgBtn><PgBtn>›</PgBtn>
          </div>
        </div>
      </div>

      <ComplianceDrawer listingId={checklist?.id ?? null} listingLabel={checklist?.label} onClose={() => setChecklist(null)} />
      <KeysDrawer listingId={keysFor?.id ?? null} listingLabel={keysFor?.label} onClose={() => setKeysFor(null)} />
    </div>
  );
}

function Stat({ v, unit, l, delta, last }: { v: string; unit?: string; l: string; delta?: string; last?: boolean }): JSX.Element {
  return (
    <div className={`p-[16px_18px] ${last ? "" : "border-r border-rule-soft"}`}>
      <div className="font-mono text-[25px] font-semibold tracking-tight text-ink">{v}{unit ? <span className="text-[14px] font-medium text-ink-mute">{unit}</span> : null}</div>
      <div className="micro mt-[5px]">{l}</div>
      {delta ? <div className="mt-[7px] text-[11px] text-success">↗ {delta}</div> : null}
    </div>
  );
}
function Th({ children, className = "" }: { children: React.ReactNode; className?: string }): JSX.Element {
  return <th className={`border-b border-rule p-[11px_14px] text-left font-mono text-[10px] font-medium uppercase tracking-[0.1em] text-ink-mute ${className}`}>{children}</th>;
}
function PgBtn({ children, active }: { children: React.ReactNode; active?: boolean }): JSX.Element {
  return <span className={`grid size-[30px] cursor-pointer place-items-center rounded-[5px] border font-mono text-[12px] ${active ? "border-accent bg-accent/10 text-accent" : "border-rule text-ink-soft"}`}>{children}</span>;
}
